'use client';

import { useEffect, useState } from 'react';
import {
  getLabelColumnWidthClass,
  type TableDensity,
} from '@/new_components/shared/tableLayout';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LocaleLink } from '@/components/locale-link';
import {
  type RawFinancialResponse,
  type RawFinancialRow,
  type RawFinancialSectionKey,
} from './financialRawService';

type Density = TableDensity;

export interface RawFinancialsCardProps {
  euId: string;
  className?: string;
  title?: string;
  subtitle?: string;
  showHeader?: boolean;
  density?: Density;
}

const SECTION_LABELS: Record<RawFinancialSectionKey, string> = {
  aktiva: 'Aktiva (Raw)',
  passiva: 'Passiva (Raw)',
  guv: 'GUV (Raw)',
};

const SECTION_ORDER: RawFinancialSectionKey[] = ['aktiva', 'passiva', 'guv'];

export default function RawFinancialsCard({
  euId,
  className,
  title = 'Raw Financial Data',
  subtitle = 'Per year, ordered by filing index',
  showHeader = true,
  density = 'default',
}: RawFinancialsCardProps) {
  const [financialRaw, setFinancialRaw] = useState<RawFinancialResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setPermissionDenied(false);
      try {
        const response = await fetch(
          `/api/companies/${euId}/financial-tables/raw`,
        );
        if (response.status === 403) {
          setPermissionDenied(true);
        } else if (response.ok) {
          const json = await response.json();
          setFinancialRaw(json.data);
        }
      } catch {
        // silently handle fetch errors
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [euId]);

  if (loading) {
    return (
      <section
        className={cn(
          'card-panel raw-financials-card',
          density === 'compact' && 'raw-financials-card-compact',
          className,
        )}
      >
        {showHeader && (
          <div className="section-header">
            <h3>{title}</h3>
            <span className="section-subtitle">{subtitle}</span>
          </div>
        )}
        <p className="text-muted mb-0">Loading raw financial data...</p>
      </section>
    );
  }

  if (permissionDenied) {
    return (
      <section
        className={cn(
          'card-panel raw-financials-card',
          density === 'compact' && 'raw-financials-card-compact',
          className,
        )}
        data-nosnippet
      >
        {showHeader && (
          <div className="section-header">
            <h3>{title}</h3>
            <span className="section-subtitle">{subtitle}</span>
          </div>
        )}
        <div className="flex flex-col items-center justify-center py-12 px-6">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="rounded-full bg-muted p-4">
              <Lock className="h-8 w-8 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">
                Please Upgrade to View
              </h3>
              <p className="text-sm text-muted-foreground max-w-md">
                This section requires a higher subscription tier. Upgrade your
                plan to access this content.
              </p>
            </div>
            <Button asChild variant="primary" size="md" className="mt-2">
              <LocaleLink href="/plans">Upgrade</LocaleLink>
            </Button>
          </div>
        </div>
      </section>
    );
  }

  if (!financialRaw) {
    return (
      <section
        className={cn(
          'card-panel raw-financials-card',
          density === 'compact' && 'raw-financials-card-compact',
          className,
        )}
      >
        {showHeader && (
          <div className="section-header">
            <h3>{title}</h3>
            <span className="section-subtitle">{subtitle}</span>
          </div>
        )}
        <p className="text-muted mb-0">No raw financial data available.</p>
      </section>
    );
  }

  const hasAnyData = SECTION_ORDER.some(
    (section) => Object.keys(financialRaw[section]).length > 0,
  );

  if (!hasAnyData) {
    return (
      <section
        className={cn(
          'card-panel raw-financials-card',
          density === 'compact' && 'raw-financials-card-compact',
          className,
        )}
      >
        {showHeader && (
          <div className="section-header">
            <h3>{title}</h3>
            <span className="section-subtitle">{subtitle}</span>
          </div>
        )}
        <p className="text-muted mb-0">No raw financial data available.</p>
      </section>
    );
  }

  return (
    <section
      className={cn(
        'card-panel raw-financials-card',
        density === 'compact' && 'raw-financials-card-compact',
        className,
      )}
    >
      {showHeader && (
        <div className="section-header">
          <h3>{title}</h3>
          <span className="section-subtitle">{subtitle}</span>
        </div>
      )}

      <div className="raw-financials-card-content space-y-6">
        {SECTION_ORDER.map((sectionKey) =>
          renderSection(sectionKey, financialRaw, density),
        )}
      </div>

      <style>{rawFinancialStyles}</style>
    </section>
  );
}

function renderSection(
  section: RawFinancialSectionKey,
  data: RawFinancialResponse,
  density: Density,
) {
  const yearMap = data[section];
  const years = Object.keys(yearMap)
    .filter(Boolean)
    .sort((a, b) => {
      const numA = Number(a);
      const numB = Number(b);
      if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
        return numB - numA;
      }
      return b.localeCompare(a, 'de-DE');
    });

  if (!years.length) {
    return null;
  }

  return (
    <div key={section} className="raw-financial-section">
      <div className="raw-financial-section-header">
        <div>
          <h4 className="raw-financial-section-title">
            {SECTION_LABELS[section]}
          </h4>
          <p className="raw-financial-section-subtitle">
            {years.length} {years.length === 1 ? 'year' : 'years'} available
          </p>
        </div>
      </div>

      <Tabs defaultValue={years[0]} className="w-full">
        <TabsList
          variant="button"
          size="sm"
          className="raw-financial-tabs-list overflow-x-auto"
        >
          {years.map((year) => (
            <TabsTrigger
              key={`${section}-${year}`}
              value={year}
              className="min-w-12"
            >
              {year}
            </TabsTrigger>
          ))}
        </TabsList>

        {years.map((year) => {
          let rows = yearMap[year];
          if (section === 'aktiva' && rows) {
            rows = rows.filter((r) => r.label !== 'Passiva');
          }
          return (
            <TabsContent key={`${section}-content-${year}`} value={year}>
              {renderTable(rows, year, density)}
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}

function renderTable(
  rows: RawFinancialRow[] | undefined,
  year: string,
  density: Density,
) {
  const sortedRows = sortRows(rows);

  if (!sortedRows.length) {
    return (
      <p className="text-sm text-muted-foreground">
        No rows available for {year}.
      </p>
    );
  }

  // Use top 2 rows as header/subheader
  const headerRows = sortedRows.slice(0, 2);
  const dataRows = sortedRows.slice(2);

  const isCompact = density === 'compact';
  const labelWidthClass = getLabelColumnWidthClass(density);
  const baseCellClass = cn(
    'border border-[#ccc] bg-transparent whitespace-nowrap align-middle',
    isCompact ? 'px-1.5 py-1 text-xs' : 'px-2 py-1.5 text-sm',
  );
  const valueWidthClass = 'min-w-[120px]';
  const headerLabelClass = cn(
    baseCellClass,
    labelWidthClass,
    'text-left font-semibold',
  );
  const headerValueClass = cn(
    baseCellClass,
    valueWidthClass,
    'text-right font-semibold',
  );
  const labelCellClass = cn(
    'raw-financial-label',
    baseCellClass,
    labelWidthClass,
    'truncate pr-2 text-left font-medium text-foreground',
  );
  const valueCellClass = cn(baseCellClass, valueWidthClass, 'text-right');

  const valueKeys = [
    'val1',
    'val2',
    'val3',
    'val4',
    'val5',
    'val6',
    'val7',
    'val8',
  ] as const;
  const visibleKeys = valueKeys.filter((key) =>
    sortedRows.some((row) => {
      const val = row[key];
      return val !== null && val !== undefined && val !== '';
    }),
  );

  return (
    <div className="raw-financial-table-wrapper">
      <table
        className={cn(
          'raw-financial-table',
          density === 'compact' && 'raw-financial-table-compact',
        )}
      >
        <thead>
          {headerRows.map((headerRow, i) => (
            <tr key={`header-${i}`}>
              <th className={headerLabelClass}>
                {formatValue(headerRow.label)}
              </th>
              {visibleKeys.map((key) => (
                <th key={key} className={headerValueClass}>
                  {formatValue(headerRow[key] as string | number | null)}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {dataRows.map((row, i) => {
            const depth = deriveRowDepth(row.label);
            return (
              <tr key={`${row.label}-${row.index ?? 'na'}-${i}`}>
                <td className={labelCellClass} data-depth={depth}>
                  {row.label}
                </td>
                {visibleKeys.map((key) => (
                  <td key={key} className={valueCellClass}>
                    {formatValue(row[key])}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function sortRows(rows: RawFinancialRow[] | undefined) {
  if (!rows?.length) {
    return [];
  }

  return [...rows].sort((a, b) => {
    const idxA =
      typeof a.index === 'number' ? a.index : Number.MAX_SAFE_INTEGER;
    const idxB =
      typeof b.index === 'number' ? b.index : Number.MAX_SAFE_INTEGER;

    if (idxA !== idxB) {
      return idxA - idxB;
    }

    return (a.label || '').localeCompare(b.label || '', 'de-DE', {
      sensitivity: 'base',
    });
  });
}

function formatValue(value: string | number | null) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return String(value);
}

function deriveRowDepth(label?: string | null) {
  if (!label) {
    return 4;
  }
  const trimmed = label.trim();
  if (/^[A-Z]\.\s/.test(trimmed)) {
    return 1;
  }
  if (/^(I|V|X|L|C|D|M)+\.\s/.test(trimmed)) {
    return 2;
  }
  if (/^\d+\.\s/.test(trimmed)) {
    return 3;
  }
  return 4;
}

const rawFinancialStyles = `
.raw-financials-card {
  padding: 1rem;
}
.raw-financials-card .raw-financial-section + .raw-financial-section {
  padding-top: 0.5rem;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
}
.raw-financial-section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 0.75rem;
}
.raw-financial-section-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}
.raw-financial-section-subtitle {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: rgba(15, 23, 42, 0.6);
}
.raw-financial-tabs-list {
  margin-bottom: 0.75rem;
  padding-bottom: 0.25rem;
}
.raw-financials-card-content {
  padding-top: 0.5rem;
}
.raw-financial-table-wrapper {
  width: 100%;
  overflow-x: auto;
  padding: 0.5rem 0.25rem;
}
.raw-financial-table {
  width: auto;
  border-collapse: collapse;
  table-layout: auto;
}
.raw-financial-table th,
.raw-financial-table td {
  border: 1px solid #ccc;
  padding: 4px 8px;
  white-space: nowrap;
  background: transparent;
  text-align: left;
  vertical-align: middle;
}
.raw-financial-table td.raw-financial-label {
  padding-right: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
}
.raw-financial-table td.raw-financial-label[data-depth="0"] {
  padding-left: 8px !important;
  font-weight: 600;
}
.raw-financial-table td.raw-financial-label[data-depth="1"] {
  padding-left: 18px !important;
}
.raw-financial-table td.raw-financial-label[data-depth="2"] {
  padding-left: 30px !important;
}
.raw-financial-table td.raw-financial-label[data-depth="3"] {
  padding-left: 44px !important;
}
.raw-financial-table td.raw-financial-label[data-depth="4"] {
  padding-left: 56px !important;
}
.raw-financial-table td.raw-financial-label + td {
  padding-left: 4px;
}
.raw-financial-table tr:has(> td.raw-financial-label[data-depth="1"]) td {
  background-color: #94b8c9 !important;
}
.raw-financial-table tr:has(> td.raw-financial-label[data-depth="2"]) td {
  background-color: #c9dce4 !important;
}
.raw-financial-table tr:has(> td.raw-financial-label[data-depth="3"]) td {
  background-color: #eff5f7 !important;
}
.raw-financial-table tr:has(> td.raw-financial-label[data-depth="4"]) td {
  background-color: #ffffff !important;
}
.raw-financial-table td.text-end {
  text-align: right;
}
.raw-financial-header-unit {
  display: block;
  font-size: 0.7rem;
  color: rgba(15, 23, 42, 0.55);
}
.raw-financial-header {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}
.raw-financial-table th:first-child .raw-financial-header {
  align-items: flex-start;
}
.raw-financials-card-compact .raw-financial-table th,
.raw-financials-card-compact .raw-financial-table td {
  font-size: 12px;
  padding: 2px 6px;
}
.raw-financials-card-compact .raw-financial-section-title {
  font-size: 0.95rem;
}
`;
